import { useEffect, type RefObject } from "react";
import { isEditableShortcutTarget } from "@/editor/actions/keymap";

/** Hosts that scroll via their own wheel handlers (not CSS overflow). */
const CUSTOM_WHEEL_SCROLL_SELECTOR = [
  "[data-curva-script-editor]",
  "[data-curva-allow-wheel-scroll]",
  ".monaco-editor",
].join(", ");

export function useBrowserGestureGuards(rootRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const preventDefault = (event: Event) => {
      if (isInsideEditor(event.target, rootRef.current) && event.cancelable) {
        event.preventDefault();
      }
    };

    const preventWheelDefaults = (event: WheelEvent) => {
      const root = rootRef.current;
      if (!isInsideEditor(event.target, root) || !event.cancelable) {
        return;
      }

      // Allow trackpad/mouse wheel scroll for CSS-overflow hosts and for
      // Monaco (script editor), which scrolls without overflow:auto ancestors.
      if (!event.ctrlKey && (hasScrollableWheelTarget(event, root) || hasCustomWheelScrollTarget(event, root))) {
        return;
      }

      event.preventDefault();
    };

    const preventBrowserShortcuts = (event: KeyboardEvent) => {
      if (!rootRef.current || !event.cancelable || isEditableShortcutTarget(event.target)) {
        return;
      }

      const key = event.key.toLowerCase();
      const isZoomShortcut = (event.metaKey || event.ctrlKey) && ["+", "=", "-", "_", "0"].includes(key);
      const isHistoryShortcut =
        (event.metaKey || event.altKey) && ["arrowleft", "arrowright", "[", "]"].includes(key);

      if (isZoomShortcut || isHistoryShortcut) {
        event.preventDefault();
      }
    };

    window.addEventListener("wheel", preventWheelDefaults, { capture: true, passive: false });
    window.addEventListener("keydown", preventBrowserShortcuts, { capture: true });
    // Safari trackpad pinch fires gesture* instead of (or in addition to) wheel+ctrl.
    // Block browser page zoom here; CanvasWorkspace applies the actual camera zoom.
    window.addEventListener("gesturestart", preventDefault, { passive: false });
    window.addEventListener("gesturechange", preventDefault, { passive: false });
    window.addEventListener("gestureend", preventDefault, { passive: false });

    return () => {
      window.removeEventListener("wheel", preventWheelDefaults, { capture: true });
      window.removeEventListener("keydown", preventBrowserShortcuts, { capture: true });
      window.removeEventListener("gesturestart", preventDefault);
      window.removeEventListener("gesturechange", preventDefault);
      window.removeEventListener("gestureend", preventDefault);
    };
  }, [rootRef]);
}

function isInsideEditor(target: EventTarget | null, root: HTMLElement | null): boolean {
  return target instanceof Node && root?.contains(target) === true;
}

function hasScrollableWheelTarget(event: WheelEvent, root: HTMLElement | null): boolean {
  if (!(event.target instanceof Node) || !root) {
    return false;
  }

  let element = event.target instanceof HTMLElement ? event.target : event.target.parentElement;
  while (element && element !== root) {
    if (canElementScrollForWheel(element, event)) {
      return true;
    }
    element = element.parentElement;
  }

  return false;
}

function hasCustomWheelScrollTarget(event: WheelEvent, root: HTMLElement | null): boolean {
  if (!(event.target instanceof Element) || !root) {
    return false;
  }
  const host = event.target.closest(CUSTOM_WHEEL_SCROLL_SELECTOR);
  return host instanceof HTMLElement && root.contains(host);
}

function canElementScrollForWheel(element: HTMLElement, event: WheelEvent): boolean {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;
  const overflowX = style.overflowX;
  const canScrollY = isScrollableOverflow(overflowY) && element.scrollHeight > element.clientHeight;
  const canScrollX = isScrollableOverflow(overflowX) && element.scrollWidth > element.clientWidth;

  return (Math.abs(event.deltaY) >= Math.abs(event.deltaX) && canScrollY) || (Math.abs(event.deltaX) > Math.abs(event.deltaY) && canScrollX);
}

function isScrollableOverflow(value: string): boolean {
  return value === "auto" || value === "scroll" || value === "overlay";
}
